import { juvareColors } from '@juvare/common-web-ui/JuvareTheme';
import { Alert, Box, Button, Divider, LinearProgress, Typography } from '@mui/material';
import React from 'react';
import { useInboxClient, useInboxTranslation } from '../Hooks';
import { NotificationListItem } from '../Models';
import { RowContent } from './Components/RowContent';

export interface InboxWidgetProps {
    title: string;
    actionTitle: string;
    onActionClick: () => void;
    onRowActionClick: (notification: NotificationListItem) => void;
    refreshIntervalInMinutes: number;
    'data-test'?: string;
}

export const InboxWidget: React.FC<InboxWidgetProps> = (props) => {
    const { t } = useInboxTranslation();
    const inboxClient = useInboxClient();
    const [notifications, setNotifications] = React.useState<NotificationListItem[]>();
    const [error, setError] = React.useState();

    React.useEffect(() => {
        const refreshNotifications = () => {
            inboxClient
                .getList({ size: 3, orderBy: 'received' })
                .then((response) => {
                    setNotifications(response.items);
                })
                .catch(setError);
        };

        refreshNotifications();

        const timer = setInterval(
            refreshNotifications,
            (props.refreshIntervalInMinutes < 1 ? 1 : props.refreshIntervalInMinutes) * 1000 * 60,
        );

        return () => clearInterval(timer);
    }, [inboxClient, props.refreshIntervalInMinutes]);

    const InboxWidgetContent = () => {
        if (error) {
            return <Alert severity='error'>{t('errorMessage')}</Alert>;
        }

        if (!notifications) {
            return <LinearProgress />;
        }

        return (
            <Box>
                {notifications.map((notification, index) => {
                    return (
                        <Box key={notification.id}>
                            <Divider variant={index > 0 ? 'middle' : undefined} />
                            <RowContent
                                key={notification.id}
                                dateReceived={notification.received}
                                title={notification.subject}
                                isRead={!!notification.read}
                                onActionClick={() => {
                                    props.onRowActionClick(notification);
                                }}
                            />
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box sx={{ border: 1, borderRadius: 1, borderColor: juvareColors.neutral[300] }} data-test={props['data-test']}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'common.white',
                    p: 2,
                }}
            >
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        justifyContent: 'flex-start',
                    }}
                >
                    <Typography variant='subtitle2'>{props.title}</Typography>
                </Box>
                <Box sx={{ flexShrink: 0, display: 'flex', flexWrap: 'wrap', alignSelf: 'flex-end' }}>
                    <Button onClick={props.onActionClick} variant='text'>
                        {props.actionTitle}
                    </Button>
                </Box>
            </Box>
            <InboxWidgetContent />
        </Box>
    );
};
